import React, { useState, useEffect } from 'react';

// Function to load initial form data from localStorage (or use default values)
const initialFormData = () => {
  const saved = localStorage.getItem('simulationFormData');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Error parsing simulationFormData from localStorage', e);
    }
  }
  return {
    prixAchat: 460000,
    apport: 50000,
    tauxInteret: 3,
    dureePret: 25,
    fraisNotaire: 15000,
    loyer: 1735,
    tauxAugmentation: 2,
    chargesCopro: 2000,
    taxeFonciere: 1000
  };
};

const SimulationForm = ({ runSimulation }) => {
  const [formData, setFormData] = useState(initialFormData);

  // State to track if the user manually overrode the frais de notaire
  const [manualFraisNotaire, setManualFraisNotaire] = useState(false);

  // Function to calculate realistic notary fees (approx. 8% of the purchase price)
  const calculateFraisNotaire = (prixAchat) => {
    const price = parseFloat(prixAchat);
    if (isNaN(price) || price <= 0) return '';
    // For a realistic estimation, notary fees for an old property are around 7-8% of the purchase price
    return (price * 0.08).toFixed(2);
  };

  // When the purchase price changes, update the frais de notaire if the user hasn't overridden manually
  useEffect(() => {
    if (!manualFraisNotaire) {
      setFormData(prev => ({ ...prev, fraisNotaire: calculateFraisNotaire(prev.prixAchat) }));
    }
  }, [formData.prixAchat, manualFraisNotaire]);

  // Sauvegarde automatiquement les données du formulaire dans le localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('simulationFormData', JSON.stringify(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fraisNotaire') {
      setManualFraisNotaire(true);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetFraisNotaire = () => {
    setManualFraisNotaire(false);
    setFormData(prev => ({ ...prev, fraisNotaire: calculateFraisNotaire(prev.prixAchat) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSimulation(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* Prix d'achat */}
        <div>
          <label htmlFor="prixAchat" className="block text-sm font-medium text-gray-700">Prix d'achat</label>
          <input
            id="prixAchat"
            type="number"
            name="prixAchat"
            value={formData.prixAchat}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Apport */}
        <div>
          <label htmlFor="apport" className="block text-sm font-medium text-gray-700">Apport</label>
          <input
            id="apport"
            type="number"
            name="apport"
            value={formData.apport}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Taux d'intérêt */}
        <div>
          <label htmlFor="tauxInteret" className="block text-sm font-medium text-gray-700">Taux d'intérêt (%)</label>
          <input
            id="tauxInteret"
            type="number"
            name="tauxInteret"
            value={formData.tauxInteret}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Durée du prêt */}
        <div>
          <label htmlFor="dureePret" className="block text-sm font-medium text-gray-700">Durée du prêt (années)</label>
          <input
            id="dureePret"
            type="number"
            name="dureePret"
            value={formData.dureePret}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Frais de notaire */}
        <div>
          <label htmlFor="fraisNotaire" className="block text-sm font-medium text-gray-700">Frais de notaire</label>
          <input
            id="fraisNotaire"
            type="number"
            name="fraisNotaire"
            value={formData.fraisNotaire}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
          {manualFraisNotaire && (
            <button type="button" onClick={resetFraisNotaire} className="mt-2 text-xs text-blue-500 underline">
              Réinitialiser
            </button>
          )}
        </div>
        {/* Loyer */}
        <div>
          <label htmlFor="loyer" className="block text-sm font-medium text-gray-700">Loyer</label>
          <input
            id="loyer"
            type="number"
            name="loyer"
            value={formData.loyer}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Taux d'augmentation du loyer */}
        <div>
          <label htmlFor="tauxAugmentation" className="block text-sm font-medium text-gray-700">Taux d'augmentation du loyer (% annuel)</label>
          <input
            id="tauxAugmentation"
            type="number"
            name="tauxAugmentation"
            value={formData.tauxAugmentation}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Charges de copropriété annuelles */}
        <div>
          <label htmlFor="chargesCopro" className="block text-sm font-medium text-gray-700">Charges de copropriété annuelles</label>
          <input
            id="chargesCopro"
            type="number"
            name="chargesCopro"
            value={formData.chargesCopro}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {/* Taxe foncière annuelle */}
        <div>
          <label htmlFor="taxeFonciere" className="block text-sm font-medium text-gray-700">Taxe foncière annuelle</label>
          <input
            id="taxeFonciere"
            type="number"
            name="taxeFonciere"
            value={formData.taxeFonciere}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="text-center">
        <button type="submit" className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-md shadow">
          Lancer la simulation
        </button>
      </div>
    </form>
  );
};

export default SimulationForm; 